import template from './jga-revocation-consent-list.html.twig';
import './jga-revocation-consent-list.scss';

const { Component } = Shopware;
const { Criteria } = Shopware.Data;

const FALLBACK_LOCALE = 'en-GB';

// DOMPurify in sw-text-editor strips href attributes with non-standard schemes.
// We bridge the magic links (cms://x = modal fragment, page://x = full page)
// through a recognised https placeholder while editing and convert back when
// storing, so the magic-link convention survives the editor round-trip.
const MAGIC_SCHEMES = ['cms', 'page'];
const MAGIC_TARGETS = ['privacy', 'tos', 'imprint'];

function bridgeHost(scheme, target) {
    return `https://__${scheme}-${target}__.invalid/`;
}

function bridgeForEditor(html) {
    if (!html) return html;
    let out = html;
    for (const scheme of MAGIC_SCHEMES) {
        for (const target of MAGIC_TARGETS) {
            out = out.split(`${scheme}://${target}`).join(bridgeHost(scheme, target));
        }
    }
    return out;
}

function unbridgeFromEditor(html) {
    if (!html) return html;
    let out = html;
    for (const scheme of MAGIC_SCHEMES) {
        for (const target of MAGIC_TARGETS) {
            const magic = `${scheme}://${target}`;
            const bridge = bridgeHost(scheme, target);
            // Match with or without trailing slash in case the editor normalised the URL
            out = out.split(bridge).join(magic);
            out = out.split(bridge.slice(0, -1)).join(magic);
        }
    }
    return out;
}

function generateId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().replace(/-/g, '');
    }
    return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

Component.register('jga-revocation-consent-list', {
    template,

    inject: ['systemConfigApiService', 'repositoryFactory'],

    props: {
        value: {
            type: Array,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default: null,
        },
        helpText: {
            type: String,
            required: false,
            default: null,
        },
    },

    emits: ['update:value'],

    data() {
        return {
            editingItem: null,
            editingItemIndex: null,
            activeLocale: null,
            confirmDeleteIndex: null,
            languages: [],
        };
    },

    created() {
        this.loadLanguages();
    },

    mounted() {
        this._keydownHandler = (event) => {
            if (event.key !== 'Escape' || !this.editingItem) return;
            const popover = document.querySelector('.sw-text-editor-toolbar-button__link-menu');
            if (popover) {
                this.dismissEditorPopovers();
                event.stopPropagation();
                return;
            }
            this.cancelEdit();
        };
        document.addEventListener('keydown', this._keydownHandler, true);

        // Close any open text-editor popover when clicking the modal close/cancel
        // controls. sw-text-editor's outside-click handler is unreliable when the
        // popover overlaps the modal footer, so we proactively dismiss it.
        this._mousedownHandler = (event) => {
            if (!this.editingItem) return;
            const popover = document.querySelector('.sw-text-editor-toolbar-button__link-menu');
            if (!popover || popover.contains(event.target)) return;

            const target = event.target;
            const isCloseControl = target.closest?.('.sw-modal__close')
                || target.closest?.('.sw-modal__footer')
                || target.closest?.('.sw-modal__title');

            if (isCloseControl) {
                this.dismissEditorPopovers();
            }
        };
        document.addEventListener('mousedown', this._mousedownHandler, true);
    },

    unmounted() {
        if (this._keydownHandler) {
            document.removeEventListener('keydown', this._keydownHandler, true);
        }
        if (this._mousedownHandler) {
            document.removeEventListener('mousedown', this._mousedownHandler, true);
        }
    },

    computed: {
        languageRepository() {
            return this.repositoryFactory.create('language');
        },

        items() {
            const list = Array.isArray(this.value) ? this.value : [];
            return [...list].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        },

        languageOptions() {
            const fromShop = this.languages.map((lang) => ({ value: lang.code, label: lang.name }));
            const seen = new Set(fromShop.map((o) => o.value));
            const extras = [];
            if (this.editingItem?.label) {
                Object.keys(this.editingItem.label).forEach((code) => {
                    if (!seen.has(code)) {
                        extras.push({ value: code, label: code });
                    }
                });
            }
            return [...fromShop, ...extras];
        },
    },

    methods: {
        async loadLanguages() {
            try {
                const criteria = new Criteria(1, 100);
                criteria.addAssociation('locale');
                criteria.addSorting(Criteria.sort('name', 'ASC'));
                const result = await this.languageRepository.search(criteria, Shopware.Context.api);
                const systemLanguageId = Shopware.Context?.api?.systemLanguageId || null;
                const all = [...result]
                    .map((lang) => ({
                        id: lang.id,
                        name: lang.name,
                        code: lang.locale?.code || null,
                        isDefault: systemLanguageId !== null && lang.id === systemLanguageId,
                    }))
                    .filter((l) => l.code);
                // System default language first so it acts as the initial selection.
                this.languages = [
                    ...all.filter((l) => l.isDefault),
                    ...all.filter((l) => !l.isDefault),
                ];
            } catch (e) {
                this.languages = [];
            }
        },

        cleanLabel(htmlString) {
            if (!htmlString) {
                return '';
            }
            const tmp = document.createElement('div');
            tmp.innerHTML = htmlString;
            return (tmp.textContent || '').trim();
        },

        labelPreview(item) {
            const text = item?.label?.[this.activeLocale]
                || item?.label?.['de-DE']
                || item?.label?.['en-GB']
                || Object.values(item?.label || {}).find((value) => typeof value === 'string')
                || '';

            const stripped = this.cleanLabel(text);
            if (stripped.length <= 80) {
                return stripped;
            }
            return `${stripped.substring(0, 77)}…`;
        },

        emitItems(items) {
            const sorted = items
                .map((item, index) => ({ ...item, position: index + 1 }))
                .sort((a, b) => a.position - b.position);

            this.$emit('update:value', sorted);
            this.$emit('change', sorted);
        },

        async addItem() {
            await this.loadLanguages();
            const labelMap = {};
            this.languages.forEach((lang) => { labelMap[lang.code] = ''; });
            if (Object.keys(labelMap).length === 0) {
                labelMap[FALLBACK_LOCALE] = '';
            }
            const newItem = {
                id: generateId(),
                label: labelMap,
                required: true,
                active: true,
                displayAsText: false,
                position: this.items.length + 1,
            };
            this.editingItem = JSON.parse(JSON.stringify(newItem));
            this.editingItemIndex = -1;
            this.activeLocale = this.defaultActiveLocale();
        },

        async editItem(index) {
            const item = this.items[index];
            if (!item) {
                return;
            }
            await this.loadLanguages();
            this.editingItem = JSON.parse(JSON.stringify(item));
            const labelMap = { ...(this.editingItem.label || {}) };
            this.languages.forEach((lang) => {
                if (!Object.prototype.hasOwnProperty.call(labelMap, lang.code)) {
                    labelMap[lang.code] = '';
                }
            });
            this.editingItem.label = labelMap;
            this.editingItemIndex = index;
            this.activeLocale = this.localeWithContent(item) || this.defaultActiveLocale();
        },

        localeWithContent(item) {
            if (!item?.label) {
                return null;
            }
            const preferred = ['de-DE', 'en-GB'];
            for (const code of preferred) {
                const value = item.label[code];
                if (typeof value === 'string' && this.cleanLabel(value) !== '') {
                    return code;
                }
            }
            for (const [code, value] of Object.entries(item.label)) {
                if (typeof value === 'string' && this.cleanLabel(value) !== '') {
                    return code;
                }
            }
            return null;
        },

        defaultActiveLocale() {
            // Prefer German if available — plugin is German-centric.
            const deLang = this.languages.find((l) => l.code === 'de-DE');
            if (deLang) {
                return 'de-DE';
            }
            const defaultLang = this.languages.find((l) => l.isDefault);
            if (defaultLang?.code) {
                return defaultLang.code;
            }
            if (this.languages.length > 0) {
                return this.languages[0].code;
            }
            const labelKeys = Object.keys(this.editingItem?.label || {});
            return labelKeys[0] || FALLBACK_LOCALE;
        },

        cancelEdit() {
            this.dismissEditorPopovers();
            this.editingItem = null;
            this.editingItemIndex = null;
        },

        dismissEditorPopovers() {
            // Close link/table popovers that would otherwise survive the modal close.
            const linkButton = document.querySelector('.sw-text-editor-toolbar-button__type-link');
            if (linkButton && document.querySelector('.sw-text-editor-toolbar-button__link-menu')) {
                linkButton.click();
            }
        },

        async saveEdit() {
            if (!this.editingItem) {
                return;
            }
            this.dismissEditorPopovers();
            const cleanedLabel = {};
            Object.entries(this.editingItem.label || {}).forEach(([locale, value]) => {
                if (typeof value === 'string' && this.cleanLabel(value) !== '') {
                    cleanedLabel[locale] = value;
                }
            });
            const itemToSave = { ...this.editingItem, label: cleanedLabel };
            // A notice-only item can never be required (no checkbox to tick).
            if (itemToSave.displayAsText) {
                itemToSave.required = false;
            }

            const next = [...this.items];
            if (this.editingItemIndex === -1) {
                next.push(itemToSave);
            } else if (this.editingItemIndex !== null) {
                next.splice(this.editingItemIndex, 1, itemToSave);
            }
            this.emitItems(next);
            this.cancelEdit();

            await this.$nextTick();
            this.triggerPluginConfigSave();
        },

        triggerPluginConfigSave() {
            // Find and click the plugin-config page-level save button so the
            // user does not have to confirm twice. We exclude buttons inside
            // modals and disabled ones.
            const candidates = Array.from(document.querySelectorAll(
                '.sw-extension-config button, .smart-bar__actions button, .sw-page__smart-bar button, .sw-system-config button'
            ));
            const saveBtn = candidates.find((btn) => {
                if (btn.disabled || btn.closest('.sw-modal')) return false;
                const label = (btn.innerText || '').trim().toLowerCase();
                return label === 'speichern' || label === 'save';
            });
            saveBtn?.click();
        },

        askDelete(index) {
            this.confirmDeleteIndex = index;
        },

        cancelDelete() {
            this.confirmDeleteIndex = null;
        },

        confirmDelete() {
            if (this.confirmDeleteIndex === null) {
                return;
            }
            const next = this.items.filter((_, idx) => idx !== this.confirmDeleteIndex);
            this.emitItems(next);
            this.confirmDeleteIndex = null;
        },

        moveUp(index) {
            if (index <= 0) return;
            const next = [...this.items];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            this.emitItems(next);
        },

        moveDown(index) {
            if (index >= this.items.length - 1) return;
            const next = [...this.items];
            [next[index + 1], next[index]] = [next[index], next[index + 1]];
            this.emitItems(next);
        },

        setActive(index, value) {
            const next = this.items.map((item, idx) => (
                idx === index ? { ...item, active: this.coerceBool(value, !item.active) } : item
            ));
            this.emitItems(next);
        },

        setRequired(index, value) {
            const next = this.items.map((item, idx) => (
                idx === index ? { ...item, required: this.coerceBool(value, !item.required) } : item
            ));
            this.emitItems(next);
        },

        coerceBool(value, fallback) {
            if (typeof value === 'boolean') return value;
            if (value && typeof value.target !== 'undefined' && typeof value.target.checked === 'boolean') {
                return value.target.checked;
            }
            return fallback;
        },

        labelForLocale(locale) {
            return bridgeForEditor((this.editingItem?.label?.[locale]) ?? '');
        },

        updateLabelForLocale(locale, value) {
            if (!this.editingItem) return;
            this.editingItem = {
                ...this.editingItem,
                label: { ...this.editingItem.label, [locale]: unbridgeFromEditor(value) },
            };
        },
    },
});
